const getEnvironment = () => {
    try {
        return window.saftEnvVariables?.REACT_APP_ENVIRONMENT_TYPE || 'local';
    } catch {
        return 'unknown';
    }
};

const getUserRoles = (userInformation, organizationId) => {
    const roles = [];
    
    if (userInformation?.role) {
        roles.push(userInformation.role);
    }
    
    if (userInformation?.organizationsRoles && organizationId) {
        const orgRole = userInformation.organizationsRoles[organizationId];
        if (orgRole && !roles.includes(orgRole)) {
            roles.push(orgRole);
        }
    }
    
    return JSON.stringify(roles);
};

export const initializePendo = ({ userInformation, organizationProjects, auth }) => {
    if (typeof window.pendo === 'undefined') {
        return;
    }

    if (!userInformation || !userInformation.email) {
        return;
    }

    try {
        const environment = getEnvironment();
        const organizationId = organizationProjects?.id || '';
        
        const sovosIdentityId = auth?.user?.profile?.['sovos:guid'] || '';
        
        
        const visitorData = {
            id: sovosIdentityId || '',
            saft_id: userInformation.id || '',
            email: userInformation.email || '',
            full_name: userInformation.name || '',
            firstName: auth?.user?.profile?.given_name || userInformation.name?.split(' ')[0] || '',
            lastName: auth?.user?.profile?.family_name || userInformation.name?.split(' ').slice(1).join(' ') || '',
            role: getUserRoles(userInformation, organizationId),
            locale: auth?.user?.profile?.locale || navigator?.language || 'en',
            user_language: auth?.user?.profile?.locale?.split('-')[0] || navigator?.language?.split('-')[0] || 'en',
            has_sovos_identity: userInformation.hasSovosIdentity || false,
            has_upgraded_account: userInformation.hasUpgradedAccount || false,
            is_expired: userInformation.expired || false,
            has_access_settings: userInformation.accessSettings || false,
            environment: environment,
            pendo_created_at: new Date().toISOString(),
        };

        const accountData = {
            id: organizationId || 'no-organization',
            name: organizationProjects?.name || '',
            modules: JSON.stringify(organizationProjects?.organizationsModules || []),
            expiration_date: organizationProjects?.expirationDate || '',
            project_count: organizationProjects?.projects?.length || 0,
            project_ids: JSON.stringify(
                (organizationProjects?.projects || []).map(p => p.id)
            ),
            is_active: organizationProjects?.organizationActive || false,
            is_expired: organizationProjects?.organizationExpired || false,
            fiscal_years: JSON.stringify(organizationProjects?.fiscalYears || []),
            calendar_years: JSON.stringify(organizationProjects?.calendarYears || []),
            max_fiscal_year: organizationProjects?.maxFiscalYear || '',
            environment: environment,
        };

        window.pendo.initialize({
            visitor: visitorData,
            account: accountData,
        });

    } catch (error) {
        console.error('[Pendo] - Something went wrong when initializing:', error);
    }
};

export const updatePendoOptions = ({ userInformation, organizationProjects, auth }) => {
    if (typeof window.pendo === 'undefined' || !window.pendo.updateOptions) {
        return;
    }

    try {
        const environment = getEnvironment();
        const organizationId = organizationProjects?.id || '';
        
        const sovosIdentityId = auth?.user?.profile?.['sovos:guid'] || '';
        
        const updateData = {
            visitor: {
                id: sovosIdentityId || userInformation?.id || userInformation?.email,
                email: userInformation?.email,
                role: getUserRoles(userInformation, organizationId),
                locale: auth?.user?.profile?.locale || navigator?.language || 'en',
            },
            account: {
                id: organizationId || 'no-organization',
                name: organizationProjects?.name || '',
                modules: JSON.stringify(organizationProjects?.organizationsModules || []),
                project_count: organizationProjects?.projects?.length || 0,
                project_ids: JSON.stringify(
                    (organizationProjects?.projects || []).map(p => p.id)
                ),
                is_active: organizationProjects?.organizationActive || false,
                is_expired: organizationProjects?.organizationExpired || false,
                environment: environment,
            },
        };

        window.pendo.updateOptions(updateData);
    } catch (error) {
        console.error('[Pendo] - Something went wrong when updating options:', error);
    }
};

export const trackPendoEvent = (eventName, properties = {}) => {
    if (typeof window.pendo === 'undefined' || !window.pendo.track) {
        return;
    }

    try {
        window.pendo.track(eventName, properties);
    } catch (error) {
        console.error('[Pendo] - Something went wrong when tracking event:', error);
    }
};

export const isPendoReady = () => {
    return typeof window.pendo !== 'undefined' && window.pendo.isReady && window.pendo.isReady();
};

export const trackPageNavigation = (fromPage, toPage, additionalData = {}) => {
    const eventData = {
        from_page: fromPage,
        to_page: toPage,
        environment: getEnvironment(),
        timestamp: new Date().toISOString(),
        ...additionalData,
    };
    
    trackPendoEvent('page_navigation', eventData);
};

export const trackClick = (elementName, elementType = 'button', additionalData = {}) => {
    const eventData = {
        element_name: elementName,
        element_type: elementType,
        current_page: window.location.pathname,
        environment: getEnvironment(),
        timestamp: new Date().toISOString(),
        ...additionalData,
    };
    
    trackPendoEvent('custom_click', eventData);
};

export const trackUserAction = (actionName, properties = {}) => {
    trackPendoEvent(actionName, {
        current_page: window.location.pathname,
        environment: getEnvironment(),
        timestamp: new Date().toISOString(),
        ...properties,
    });
};

const PENDO_PARAMS_STORAGE_KEY = 'pendoGuideParams';
const PENDO_URL_PARAMS = ['pendo', 'step'];

export const savePendoParamsFromUrl = () => {
    try {
        const searchParams = new URLSearchParams(window.location.search);
        const pendoParams = PENDO_URL_PARAMS.reduce((acc, key) => {
            const value = searchParams.get(key);
            if (value !== null) {
                acc[key] = value;
            }
            return acc;
        }, {});

        if (Object.keys(pendoParams).length === 0) {
            return;
        }

        sessionStorage.setItem(PENDO_PARAMS_STORAGE_KEY, JSON.stringify(pendoParams));
    } catch {
        // sessionStorage unavailable — fail silently, guide simply won't fire
    }
};


export const buildRedirectWithPendoParams = (path) => {
    try {
        const stored = sessionStorage.getItem(PENDO_PARAMS_STORAGE_KEY);
        if (!stored) {
            return path;
        }

        sessionStorage.removeItem(PENDO_PARAMS_STORAGE_KEY);

        const pendoParams = JSON.parse(stored);
        const [pathname, existingQuery] = path.split('?');
        const searchParams = new URLSearchParams(existingQuery);

        Object.entries(pendoParams).forEach(([key, value]) => {
            searchParams.set(key, value);
        });

        const query = searchParams.toString();
        return query ? `${pathname}?${query}` : pathname;
    } catch {
        return path;
    }
};
